import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { FormDetailModel, FormSettingModel } from '../models/form.model';
import { FormFieldModel, FormFieldOption } from '../models/form-field.model';

export interface FormBuilderState {
  form: FormDetailModel | null;
  fields: FormFieldModel[];
  settings: FormSettingModel | null;
  selectedFieldKey: string | null;
  activeStep: number;
  isDirty: boolean;
  isLoading: boolean;
}

const INITIAL_STATE: FormBuilderState = {
  form: null,
  fields: [],
  settings: null,
  selectedFieldKey: null,
  activeStep: 1,
  isDirty: false,
  isLoading: false,
};

@Injectable()
export class FormBuilderService {
  private readonly state$ = new BehaviorSubject<FormBuilderState>(INITIAL_STATE);

  readonly state = this.state$.asObservable();

  /** Emite cuando un campo de selección tiene sus opciones sincronizadas desde un catálogo CRM. */
  readonly catalogSynced$ = new Subject<{ fieldKey: string; options: FormFieldOption[] }>();

  get snapshot(): FormBuilderState {
    return this.state$.getValue();
  }

  get fields(): FormFieldModel[] {
    return this.snapshot.fields;
  }

  get selectedField(): FormFieldModel | null {
    const { fields, selectedFieldKey } = this.snapshot;
    return selectedFieldKey != null ? fields.find(f => f.fieldKey === selectedFieldKey) ?? null : null;
  }

  // ── Ciclo de vida del editor ───────────────────────────────────────────────

  loadForm(form: FormDetailModel, fields: FormFieldModel[], settings: FormSettingModel | null = null): void {
    this.state$.next({ ...INITIAL_STATE, form, fields, settings });
  }

  setSettings(settings: FormSettingModel | null): void {
    this.patch({ settings });
  }

  reset(): void {
    this.state$.next(INITIAL_STATE);
  }

  setLoading(isLoading: boolean): void {
    this.patch({ isLoading });
  }

  // ── Campos ────────────────────────────────────────────────────────────────

  addField(field: FormFieldModel): void {
    const fields = [...this.snapshot.fields, field];
    this.patch({ fields, isDirty: true });
  }

  /**
   * Inserta un campo dentro del paso que indica `field.stepNumber`, en la
   * posición PER-PASO `perStepIndex` (índice tal como lo entrega el lienzo,
   * que solo renderiza los campos del paso activo). El orden global del resto
   * de pasos se conserva intacto (inc34).
   */
  insertField(field: FormFieldModel, perStepIndex: number): void {
    const fields = [...this.snapshot.fields];
    const step = field.stepNumber ?? 1;
    const positions = this.stepPositions(fields, step);

    let globalIndex: number;
    if (positions.length === 0) {
      globalIndex = fields.length;
    } else if (perStepIndex >= positions.length) {
      globalIndex = positions[positions.length - 1] + 1;
    } else {
      globalIndex = positions[perStepIndex];
    }

    fields.splice(globalIndex, 0, field);
    this.patch({ fields, isDirty: true });
  }

  updateField(fieldKey: string, changes: Partial<FormFieldModel>): void {
    const fields = this.snapshot.fields.map(f =>
      f.fieldKey === fieldKey ? { ...f, ...changes } : f
    );
    this.patch({ fields, isDirty: true });
  }

  // Igual que updateField pero sin marcar isDirty — usar cuando el llamador ya
  // persiste el cambio del campo directamente (PUT propio, p. ej. el "valor fijo"
  // de catálogo desde el panel de Mapeo CRM) y no debe disparar el autosave.
  syncField(fieldKey: string, changes: Partial<FormFieldModel>): void {
    const fields = this.snapshot.fields.map(f =>
      f.fieldKey === fieldKey ? { ...f, ...changes } : f
    );
    this.patch({ fields });
  }

  removeField(fieldKey: string): void {
    const fields = this.snapshot.fields.filter(f => f.fieldKey !== fieldKey);
    const selectedFieldKey =
      this.snapshot.selectedFieldKey === fieldKey ? null : this.snapshot.selectedFieldKey;
    this.patch({ fields, selectedFieldKey, isDirty: true });
  }

  /**
   * Reordena usando índices RELATIVOS a la lista renderizada en el lienzo
   * (`renderedKeys`, que en multi-paso es solo el paso activo). Solo se permutan
   * las posiciones globales que ocupan esos campos; el resto no se mueve (inc34).
   */
  reorderFields(fromIndex: number, toIndex: number, renderedKeys: string[]): void {
    const fields = [...this.snapshot.fields];
    const keySet = new Set(renderedKeys);
    const positions: number[] = [];
    fields.forEach((f, i) => { if (keySet.has(f.fieldKey)) positions.push(i); });
    if (fromIndex < 0 || fromIndex >= positions.length) return;

    const subset = positions.map(i => fields[i]);
    const [moved] = subset.splice(fromIndex, 1);
    const target = Math.max(0, Math.min(toIndex, subset.length));
    subset.splice(target, 0, moved);

    positions.forEach((globalIdx, k) => { fields[globalIdx] = subset[k]; });
    this.patch({ fields, isDirty: true });
  }

  /** Índices globales de los campos que pertenecen a `step`, en orden de array. */
  private stepPositions(fields: FormFieldModel[], step: number): number[] {
    const positions: number[] = [];
    fields.forEach((f, i) => { if ((f.stepNumber ?? 1) === step) positions.push(i); });
    return positions;
  }

  // ── Selección ────────────────────────────────────────────────────────────

  selectField(fieldKey: string): void {
    this.patch({ selectedFieldKey: fieldKey });
  }

  deselectField(): void {
    this.patch({ selectedFieldKey: null });
  }

  // ── Pasos (multi-step) ───────────────────────────────────────────────────

  setActiveStep(step: number): void {
    this.patch({ activeStep: step });
  }

  // ── Utilidades ───────────────────────────────────────────────────────────

  updateFormMeta(changes: Partial<FormDetailModel>): void {
    if (!this.snapshot.form) return;
    this.patch({ form: { ...this.snapshot.form, ...changes }, isDirty: true });
  }

  // Igual que updateFormMeta pero sin marcar isDirty — usar cuando el llamador
  // ya persiste el cambio directamente (PUT propio) y no debe disparar el autosave de campos.
  syncFormMeta(changes: Partial<FormDetailModel>): void {
    if (!this.snapshot.form) return;
    this.patch({ form: { ...this.snapshot.form, ...changes } });
  }

  markSaved(): void {
    this.patch({ isDirty: false });
  }

  markDirty(): void {
    this.patch({ isDirty: true });
  }

  patchFieldIds(savedFields: FormFieldModel[]): void {
    const byKey = new Map(savedFields.map(f => [f.fieldKey, f]));
    const updated = this.snapshot.fields.map(f => {
      const saved = byKey.get(f.fieldKey);
      if (!saved) return f;

      let patched = (saved.id && !f.id) ? { ...f, id: saved.id } : f;

      if (saved.options?.length && patched.options?.length) {
        const patchedOptions = patched.options.map(opt => {
          const savedOpt = saved.options!.find(o => o.optionValue === opt.optionValue);
          return (savedOpt?.id && !opt.id) ? { ...opt, id: savedOpt.id } : opt;
        });
        patched = { ...patched, options: patchedOptions };
      }

      return patched;
    });
    this.patch({ fields: updated });
  }

  fieldsForStep(step: number): FormFieldModel[] {
    return this.snapshot.fields.filter(f => f.stepNumber === step);
  }

  private patch(partial: Partial<FormBuilderState>): void {
    this.state$.next({ ...this.snapshot, ...partial });
  }
}
